import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { BOOTH_TAB_LABELS, boothPanelDomId, boothTabDomId } from '../panels';
import type { BoothBadges, BoothTabId } from '../panels';
import styles from '../BoothOverlay.module.scss?module';

interface MenuBandProps {
  tabs: BoothTabId[];
  active: BoothTabId;
  badges: BoothBadges;
  onSelect: (tab: BoothTabId) => void;
}

const countFormat = new Intl.NumberFormat('en-GB');
/** Space left of the active tab when the band scrolls it into view. */
const TAB_SCROLL_INSET = 16;

/**
 * Sticky tab band of the booth. Follows the ARIA tabs pattern: only the active tab is in the
 * tab order, arrow keys, Home and End move between tabs and select them.
 */
export default function MenuBand({
  tabs,
  active,
  badges,
  onSelect,
}: MenuBandProps) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const buttons = useRef<Partial<Record<BoothTabId, HTMLButtonElement | null>>>(
    {}
  );

  // Keep the active tab in view when the band scrolls horizontally (narrow screens). Only the
  // tab list scrolls: scrollIntoView would also move the overlay vertically.
  useEffect(() => {
    const tabList = tabsRef.current;
    const button = buttons.current[active];
    if (!tabList || !button) {
      return;
    }
    const left = button.offsetLeft - tabList.offsetLeft;
    const right = left + button.offsetWidth;
    const visibleLeft = tabList.scrollLeft;
    const visibleRight = visibleLeft + tabList.clientWidth;
    if (left < visibleLeft || right > visibleRight) {
      tabList.scrollTo({ left: left - TAB_SCROLL_INSET, behavior: 'auto' });
    }
  }, [active]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.indexOf(active);
    let next: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
    }
    if (next === null) {
      return;
    }
    event.preventDefault();
    const tab = tabs[next];
    onSelect(tab);
    buttons.current[tab]?.focus();
  };

  return (
    <div className={styles.menuBand}>
      <div
        ref={tabsRef}
        className={styles.menuTabs}
        role="tablist"
        aria-label="Booth sections"
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab) => {
          const isActive = tab === active;
          const count = badges[tab];

          return (
            <button
              key={tab}
              ref={(element) => {
                buttons.current[tab] = element;
              }}
              type="button"
              role="tab"
              id={boothTabDomId(tab)}
              aria-controls={boothPanelDomId(tab)}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={styles.menuTab}
              onClick={() => onSelect(tab)}
            >
              {BOOTH_TAB_LABELS[tab]}
              {typeof count === 'number' && (
                <span className={styles.menuBadge}>
                  {countFormat.format(count)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
